import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as HourglassIcon,
  OpenInNew as OpenInNewIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import adminCommissionService from '../../services/adminCommissionService';

const STATUS_CONFIG = {
  draft: { color: 'default', icon: <ScheduleIcon fontSize="small" />, accent: '#9e9e9e', bg: '#f5f5f5' },
  pending: { color: 'warning', icon: <HourglassIcon fontSize="small" />, accent: '#f57c00', bg: '#fff8e1' },
  link_sent: { color: 'info', icon: <SendIcon fontSize="small" />, accent: '#0288d1', bg: '#e1f5fe' },
  payment_initiated: { color: 'secondary', icon: <PaymentIcon fontSize="small" />, accent: '#7b1fa2', bg: '#f3e5f5' },
  paid: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, accent: '#2e7d32', bg: '#e8f5e9' },
  overdue: { color: 'error', icon: <WarningIcon fontSize="small" />, accent: '#c62828', bg: '#ffebee' },
  cancelled: { color: 'default', icon: <WarningIcon fontSize="small" />, accent: '#757575', bg: '#eeeeee' },
  failed: { color: 'error', icon: <WarningIcon fontSize="small" />, accent: '#b71c1c', bg: '#ffcdd2' }
};

const STATUS_OPTIONS = ['draft', 'pending', 'link_sent', 'payment_initiated', 'paid', 'overdue', 'cancelled', 'failed'];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const formatAmount = (amount, currency = 'EUR') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(Number(amount) || 0);
  } catch {
    return `${Number(amount) || 0} ${currency}`;
  }
};

const getOwnerDisplayName = (owner = {}) => owner.name || [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.email || '—';

const InfoRow = ({ label, value, valueColor, mono = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} color={valueColor || 'text.primary'} fontFamily={mono ? 'monospace' : 'inherit'} sx={{ textAlign: 'right', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const AdminCommissionInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');

  const loadInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const data = await adminCommissionService.getInvoiceDetails(id);
      setInvoice(data);
      setStatus(data.status || 'pending');
      setNotes(data.notes || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement de la facture admin');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handleBack = () => {
    navigate('/admin/payins', { state: { initialTab: 2 } });
  };

  const handleDownloadPdf = async () => {
    if (!invoice || actionLoading) return;
    setActionLoading(true);
    try {
      const blob = await adminCommissionService.downloadInvoicePdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-commission-admin-${invoice.invoice_number || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erreur lors du téléchargement du PDF');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!invoice || actionLoading) return;
    setActionLoading(true);
    try {
      await adminCommissionService.updateInvoiceStatus(invoice.id, { status, notes });
      await loadInvoice();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour du statut');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendPaymentRequest = async () => {
    if (!invoice || actionLoading) return;
    setActionLoading(true);
    try {
      await adminCommissionService.sendPaymentRequests([invoice.id]);
      await loadInvoice();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l’envoi de la demande de paiement');
    } finally {
      setActionLoading(false);
    }
  };

  const statusConfig = useMemo(() => STATUS_CONFIG[invoice?.status] || STATUS_CONFIG.pending, [invoice?.status]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>Retour aux commissions</Button>
        <Alert severity="error">{error || 'Facture introuvable'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>Retour aux commissions</Button>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, border: `1px solid ${statusConfig.accent}30` }}>
        <Box sx={{ px: 3, py: 2.5, background: `linear-gradient(135deg, ${statusConfig.bg} 0%, #ffffff 100%)`, borderBottom: `2px solid ${statusConfig.accent}30`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptIcon sx={{ color: statusConfig.accent, fontSize: 28 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">Facture admin</Typography>
              <Typography variant="h5" fontWeight={900}>{invoice.invoice_number}</Typography>
              <Typography variant="body2" color="text.secondary">{getOwnerDisplayName(invoice.owner)} • {invoice.owner?.email || '—'}</Typography>
            </Box>
          </Box>
          <Chip icon={statusConfig.icon} label={invoice.status} color={statusConfig.color} sx={{ fontWeight: 700 }} />
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" fontWeight={900} color={statusConfig.accent} sx={{ mb: 1 }}>{formatAmount(invoice.total_amount, invoice.currency)}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flexWrap="wrap">
                <Chip label={`Période ${formatDate(invoice.period_start)} → ${formatDate(invoice.period_end)}`} variant="outlined" />
                <Chip label={`Échéance ${formatDate(invoice.due_date)}`} variant="outlined" />
                <Chip label={`${invoice.payins?.length || 0} payin(s)`} variant="outlined" />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadPdf} disabled={actionLoading}>Télécharger le PDF</Button>
                <Button variant="outlined" color="warning" startIcon={<SendIcon />} onClick={handleSendPaymentRequest} disabled={actionLoading}>Renvoyer la demande</Button>
                {invoice.stripe_payment_link_url && (
                  <Button variant="outlined" startIcon={<OpenInNewIcon />} href={invoice.stripe_payment_link_url} target="_blank" rel="noreferrer">Ouvrir le lien de paiement</Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>Résumé facture</Typography>
              <InfoRow label="Propriétaire" value={getOwnerDisplayName(invoice.owner)} />
              <InfoRow label="Email" value={invoice.owner?.email || '—'} />
              <InfoRow label="Montant total" value={formatAmount(invoice.total_amount, invoice.currency)} valueColor="primary.main" />
              <InfoRow label="Période" value={`${formatDate(invoice.period_start)} → ${formatDate(invoice.period_end)}`} />
              <InfoRow label="Échéance" value={formatDate(invoice.due_date)} />
              <InfoRow label="Créée le" value={formatDateTime(invoice.created_at)} />
              <InfoRow label="Payée le" value={formatDateTime(invoice.paid_date)} valueColor="success.main" />
              <InfoRow label="Email envoyé le" value={formatDateTime(invoice.email_sent_at)} />
              <InfoRow label="Relance envoyée le" value={formatDateTime(invoice.reminder_sent_at)} />
              <InfoRow label="Lien de paiement" value={invoice.stripe_payment_link_url || '—'} mono />
              <InfoRow label="Payment intent" value={invoice.stripe_payment_intent_id || '—'} mono />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>Actions admin</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Nouveau statut</InputLabel>
                <Select label="Nouveau statut" value={status} onChange={(event) => setStatus(event.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth multiline minRows={5} label="Notes admin" value={notes} onChange={(event) => setNotes(event.target.value)} />
              <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={handleUpdateStatus} disabled={actionLoading}>Mettre à jour</Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>Payins rattachés</Typography>
              <Stack spacing={1.5}>
                {(invoice.payins || []).map((payin) => (
                  <Paper key={payin.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" fontWeight={800}>{payin.payment_reference || payin.id}</Typography>
                        <Typography variant="caption" color="text.secondary">{payin.payin_type || 'commission'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="text.secondary">Commission</Typography>
                        <Typography variant="body2">{formatAmount(payin.commission_amount, payin.currency)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="text.secondary">Taxes</Typography>
                        <Typography variant="body2">{formatAmount(payin.taxes, payin.currency)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="text.secondary">Échéance</Typography>
                        <Typography variant="body2">{formatDate(payin.due_date)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" color="text.secondary">Réservation</Typography>
                        <Typography variant="body2">{payin.reservation?.listing?.title || payin.reservation?.id || '—'}</Typography>
                      </Grid>

                      {(payin.notes || payin.metadata) && (
                        <Grid item xs={12}>
                          <Accordion disableGutters elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2" fontWeight={700}>Notes et métadonnées</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {payin.notes && (
                                <>
                                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{payin.notes}</Typography>
                                </>
                              )}
                              <Typography variant="caption" color="text.secondary">Métadonnées</Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                {payin.metadata ? JSON.stringify(payin.metadata, null, 2) : '—'}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                ))}
              </Stack>

              {!(invoice.payins || []).length && <Alert severity="info" sx={{ mt: 2 }}>Aucun payin lié à cette facture.</Alert>}
            </CardContent>
          </Card>
        </Grid>

        {invoice.metadata && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>Métadonnées facture</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(invoice.metadata, null, 2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default AdminCommissionInvoiceDetailPage;